import React, { Component } from 'react'
import styled from 'styled-components'
import get from 'lodash/get'
import classNames from 'classnames'
import { Flex, Box, Text, Icon, getPaletteColor } from 'pcln-design-system'
import PropTypes from 'prop-types'
import { retrieveTripType } from '@pcln/flodash'
import { PartnerComponent } from '@pcln/whitelabel-components'
import { WidthSkeleton } from '@pcln/fly-ui-components'

import OperatedBy from '../OperatedBy'
import FreeCancellation from '../FreeCancellation'
import getFreeCancellationMessage from '../../util/get-free-cancellation-message'
import Slice from '../Slice'
import PriceBox from '../PriceBox/'
import SaleBanner from '../SaleBanner'
import DeltaFreeMessaging from '../DeltaFreeMessaging'
import UpsellNotice from '../UpsellNotice'
import FormattedIconRow from '../FormattedIcon/FormattedIconRow'
import getSubjectToGovtApproval from '../../util/get-subject-to-government-approval/index'
import buildReadableItinDescription from '../../util/buildScreenReaderText/buildScreenReaderText'
import { getMerchandisingText } from '../../util/getMerchandisingText/getMerchandisingText'
import TravelBadge from '../TravelBadge'
import { getCabinNames, getBrandAttributesForAirline, getBrandAttributeInclusion } from './helpers'
import MerchandisingStamp from './MerchandisingStamp/MerchandisingStamp'

const StyledItinerary = styled.li`
  font-family: 'Montserrat', San Francisco, Roboto;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  border-radius: ${(props) => props.theme.borderRadii.xl};
  border: 1px solid
    ${(props) =>
      props.bestValueFlight
        ? getPaletteColor('promoPrimary.base')(props)
        : getPaletteColor('border.base')(props)};
  background-color: ${getPaletteColor('background.lightest')};
  box-shadow: ${(props) => (!props.bestValueFlight ? '0 0 2px 1px rgba(0, 0, 0, 0.12)' : 'none')};
  padding: 0;

  @media (min-width: 360px) {
    width: 94%;
    ${(props) => (props.isCachedListings ? 'margin: 10px' : 'margin: 0px 10px 0px 10px')};
  }

  @media (min-width: 411px) {
    width: 95%;
  }

  ${(props) => props.theme.flyQueries.tablet} {
    ${(props) => (props.isCachedListings ? 'margin: 10px 0px 10px 0px' : 'margin: 0px')};
    width: 100%;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border: 1px solid ${getPaletteColor('border.dark')};
    ${(props) => props.hideCtaButton && 'cursor: pointer'};
    box-shadow: ${(props) => props.theme.shadows.xl};
  }
`

const Zone = styled(Flex)`
  width: 100%;
`

const TopZone = styled(Zone)`
  display: ${(props) => (props.isHidden ? 'none' : 'flex')};
  flex-direction: column;
  border-bottom: 1px solid ${getPaletteColor('border.base')};
  padding: 8px 12px;

  [data-test='sale-banner'] {
    margin: 8px 0;
  }

  ${(props) => props.theme.flyQueries.tablet} {
    display: flex;
    flex-direction: row;
  }
`

const BestValueFlightTopZone = styled(Zone)`
  display: flex;
  padding: 8px 12px;
  color: ${getPaletteColor('text.lightest')};
  background: ${getPaletteColor('promoPrimary.base')};
`

const BottomZone = styled(Zone)`
  flex-direction: column;
  border-top: 1px solid ${getPaletteColor('border.base')};
  padding: 8px 12px;

  ${(props) => props.theme.flyQueries.desktop} {
    padding: 12px 16px 12px;
  }
`

const MainZone = styled(Zone)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border: none;
  padding: 0;
`

const CugBtn = styled.button`
  align-self: center;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  margin-left: auto;
  padding: 0 4px;
  display: none;

  ${(props) => props.theme.flyQueries.tablet} {
    display: inline-block;
  }
`

const CugMobileLockIcon = styled(Icon)`
  margin-bottom: 5px;

  ${(props) => props.theme.flyQueries.tablet} {
    display: none;
  }
`

const SignInText = styled(Text.span)`
  font-size: 11px;
  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 12px;
  }
`

const AltAirportText = styled(Text.span)`
  font-size: 11px;
  color: ${getPaletteColor('alert.base')};
  font-weight: bold;
  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 12px;
  }
`

const StyledItinSlices = styled(Box)`
  flex-basis: 80%;
`

const GovernmentApproval = styled(Text.p)`
  display: ${(props) => (props.isSubjectToGovtApproval ? 'block' : 'none')};
  padding: 0 12px 8px 12px;
  color: ${getPaletteColor('text.light')};
  margin-bottom: 0;
  font-size: 10px;
  font-weight: 300;

  ${(props) => props.theme.flyQueries.desktop} {
    padding: 0 16px 8px 16px;
  }
`

const PriceBoxContainer = styled(Flex)`
  display: flex;
  justify-content: flex-end;
  padding: 0 8px 8px 0px;
  flex-basis: 20%;
  align-content: center;
  flex-wrap: wrap;
  ${(props) =>
    props.hideCtaButton &&
    `
    margin: auto 6px auto 0;
    `}
  ${(props) => props.theme.flyQueries.tablet} {
    padding-right: 24px;
  }
  ${(props) => props.theme.flyQueries.desktop} {
    padding-right: 16px;
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    padding: 16px 12px 16px;
    align-self: flex-start;
  }
`

const MobileText = styled(Text.span)`
  display: inline;
  ${(props) => props.theme.flyQueries.tabletlg} {
    display: none;
  }
`

const DesktopText = styled(Text.span)`
  display: none;
  ${(props) => props.theme.flyQueries.tabletlg} {
    display: inline;
  }
`

const IconWrapper = styled(Box)`
  display: none;
  height: 20px;
  vertical-align: middle;
  ${(props) => props.theme.flyQueries.desktop} {
    display: inline-block;
  }
`

class Itinerary extends Component {
  getChildContext = () => ({
    gaIndex: this.props.gaIndex,
  })

  render() {
    const {
      itinerary,
      gaIndex,
      hasNoHoverTooltipCookie,
      setNoTooltipOnHoverCookie,
      isUserLoggedIn,
      listingIsLoading,
      openLoginModal,
      position,
      listingsSaleStyle,
      airportToCity,
      showCUGMobileLock,
      outBoundFlight,
      isRTSliceBySlice,
      brandReference,
      minimumRetailPrice,
      departingFrom,
      shouldShowMerchBadgeAndPkgDiscount,
      hideCtaButton,
      isCachedListings,
      showNoChangesBadge,
      searchRequestCabinClass,
      isPackageVIPDiscount,
      showAirportToolTip,
      listingCardPromotion,
    } = this.props
    const { marketingAirlines, openJaw, merchandising } = itinerary

    // Get merchandising text
    const itinMerchandising = getMerchandisingText({
      merchandisingList: merchandising,
      flyGaLogic: this.context.flyGaLogic,
      departingFrom,
    })

    const isMultiAirlines = marketingAirlines?.some((airline, index, airlines) => {
      return airline.code !== airlines[0].code
    })

    const tripType = retrieveTripType(itinerary.slices)
    const imageUrl = 'https://s1.pclncdn.com/design-assets/fly/carrier-logos/'

    //  Compute screen reader text for itin details
    const srText = buildReadableItinDescription(itinerary, tripType)

    // DATA_SORT_FLIGHT_SERVICE_INFRASTRUCTURE_AA_TEST - AIR-14779
    const bestValueFlight = itinMerchandising && itinMerchandising.label === 'BEST_VALUE'

    const {
      allFareBrandAttributes,
      allFareBrandNames,
      alternateDatesTexts,
      voidWindowInfo,
      isInterline,
      isFused,
      totalPriceWithDecimal,
      operatedByAirlines,
      isSaleEligible,
      saleSavings,
      alternateType,
    } = itinerary

    const isSubjectToGovtApproval = getSubjectToGovtApproval(itinerary)
    const isAltDates = !!alternateDatesTexts
    const { onSelect } = this.context
    const voidWindowMessage = voidWindowInfo ? getFreeCancellationMessage(voidWindowInfo) : null
    const freeCancellation = {
      message: voidWindowMessage,
      className: 'free-cancellation-message',
    }

    const mobileSliceFilter = get(this.context, 'SETI.AIR_SEARCH_SLICE_FILTERS_MOBILE.isVariant', false)

    const freeCancellationMessage = voidWindowMessage && this.props.searchReturnedExpressDeals && (
      <FreeCancellation message={freeCancellation.message} messageClass={freeCancellation.className} />
    )

    const slices = itinerary.slices.map((slice, index) => (
      <Slice
        airlines={marketingAirlines}
        key={index}
        itinIndex={gaIndex}
        slice={slice}
        sliceIndex={index}
        imageUrl={imageUrl}
        tripType={tripType}
        itinerary={itinerary}
        openJaw={openJaw}
        isAltDates={isAltDates}
        hasNoHoverTooltipCookie={hasNoHoverTooltipCookie}
        setNoTooltipOnHoverCookie={setNoTooltipOnHoverCookie}
        airportToCity={airportToCity}
        GDSCitySuggestion={slice.GDSCitySuggestion}
        alternateType={alternateType}
        mobileSliceFilter={mobileSliceFilter}
        showAirportToolTip={showAirportToolTip}
      />
    ))

    const showFlightSale = isSaleEligible

    const displayMerchandising =
      itinMerchandising &&
      itinMerchandising.shortText &&
      (!showFlightSale || shouldShowMerchBadgeAndPkgDiscount)
    const isCugEligible = itinerary.isCugEligible
    let cugStrikeThroughPrice = 0

    if (showFlightSale || (isCugEligible && isUserLoggedIn)) {
      cugStrikeThroughPrice = get(itinerary, 'strikethroughPrice', 0)
    }
    let cugEligibleText = ''
    const { dollar, cents } = totalPriceWithDecimal
    const baseFare = parseFloat(`${dollar.replace(/\$|,/g, '')}.${cents}`)
    const savingsPercent =
      cugStrikeThroughPrice > 0
        ? Math.floor(((cugStrikeThroughPrice - baseFare) / cugStrikeThroughPrice) * 100)
        : 0
    let savingsDollars = cugStrikeThroughPrice > 0 ? cugStrikeThroughPrice - baseFare : 0

    // Only show decimals if non-zero
    if (savingsDollars % 1 !== 0) {
      savingsDollars = savingsDollars.toFixed(2)
    }

    /* istanbul ignore next */
    const saleBadgeText =
      listingsSaleStyle && listingsSaleStyle.badgeText
        ? listingsSaleStyle.badgeText
            .replace('$price', `$${saleSavings ? saleSavings.toFixed(2) : ''}`)
            .replace('$percent', `${savingsPercent}%`)
        : ''
    /* istanbul ignore next */
    const saleCopyText =
      listingsSaleStyle && listingsSaleStyle.copyText
        ? listingsSaleStyle.copyText
            .replace('$price', `$${saleSavings ? saleSavings.toFixed(2) : ''}`)
            .replace('$percent', `${savingsPercent}%`)
        : ''

    if (isCugEligible) {
      const percentSavingsThreshold = 1
      const dollarSavingsThreshold = 1
      let savingsText = ''
      // Percent and dollar savings
      if (savingsDollars >= dollarSavingsThreshold && savingsPercent >= percentSavingsThreshold) {
        savingsText = `Save $${savingsDollars}, that's ${savingsPercent}%!`
      } else if (savingsDollars < dollarSavingsThreshold && savingsPercent >= percentSavingsThreshold) {
        savingsText = `Save ${savingsPercent}%`
      } else if (savingsPercent < percentSavingsThreshold && savingsDollars >= dollarSavingsThreshold) {
        savingsText = `Save $${savingsDollars}`
      }
      cugEligibleText = isUserLoggedIn ? savingsText : 'Sign In'
    }

    const isCUGinMerchandisingSection = false

    const cugSection = isCugEligible && !isCUGinMerchandisingSection && (
      <CugBtn onClick={isUserLoggedIn ? () => {} : openLoginModal} data-test='cug-eligible'>
        <Text.span color='primary.dark' fontSize={0} pr={1} bold fontWeight='bold' dark>
          Member Deal
        </Text.span>
        <Text.span color='primary.base' fontSize={0} data-test='cug-eligible-text'>
          {cugEligibleText}
        </Text.span>
      </CugBtn>
    )

    const noChangeBadgeSection = showNoChangesBadge && !isMultiAirlines && <TravelBadge />

    let merchandisingSection = null
    if (displayMerchandising) {
      merchandisingSection = (
        <MerchandisingStamp
          icon={itinMerchandising.icon}
          info={itinMerchandising.shortText}
          details={itinMerchandising.longText}
          hasTravelBadge={noChangeBadgeSection}
        />
      )
    }

    const altAirportMerchandingSection = (
      <Flex align='center' alignItems='center' data-test='gds-city-suggestion-merchandising'>
        <Icon legacy={false} name='airplane' color='alert.base' data-test='gds-city-airplane' />
        <AltAirportText>Alternate Airport</AltAirportText>
        <SignInText>&nbsp;Flexible? Consider a nearby airport and save!</SignInText>
      </Flex>
    )

    const isAltAirportInMerchandisingSection = itinerary.slices.find(
      (slice) =>
        slice.GDSCitySuggestion &&
        (slice.GDSCitySuggestion.differentOrigin || slice.GDSCitySuggestion.differentDestination)
    )

    if (isAltAirportInMerchandisingSection) {
      merchandisingSection = altAirportMerchandingSection
    }

    const handleClick = () => {
      if (!isCachedListings) {
        onSelect({
          id: itinerary.id,
          gaIndex,
          itinerary,
          cabinNames: getCabinNames(itinerary, brandReference),
        })
      }
    }

    const itineraryLevelCssClasses = classNames('pcln1-retail-flight', {
      'pcln1-fused-itin': isFused,
      'pcln1-interline-itin': isInterline,
      'pcln1-open-jaw-itin': itinerary.openJaw !== null,
    })

    /** TODO: SUPER HACKY RIGHT NOW LET'S GET SOME REAL DATA */
    let upsellNotice
    const fareBrandAttributes = getBrandAttributesForAirline(
      get(allFareBrandAttributes, 0, []),
      marketingAirlines
    )

    if (itinerary.upsellNotice) {
      upsellNotice = (
        <UpsellNotice
          notice={itinerary.upsellNotice}
          fareBrandNames={allFareBrandNames}
          fareBrandAttributes={fareBrandAttributes}
          isFused={isFused}
          searchRequestCabinClass={searchRequestCabinClass}
        />
      )
    }

    const deltaFreeMessaging = itinerary.showDeltaFreeMessaging && <DeltaFreeMessaging />

    const PartnerCardMerchandising = <PartnerComponent id='CARD_MERCHANDISING_COMPONENT' />

    const formattedIconRowContent = Array.isArray(fareBrandAttributes) && !!fareBrandAttributes.length && (
      <IconWrapper p={0} mr={'4px'} mt={'2px'}>
        <FormattedIconRow
          checkedBagAvailability={getBrandAttributeInclusion(fareBrandAttributes, 'CHECKED_BAG')}
          seatAvailability={getBrandAttributeInclusion(fareBrandAttributes, 'SEATS')}
          carryOnAvailability={getBrandAttributeInclusion(fareBrandAttributes, 'CARRY_ON')}
        />
      </IconWrapper>
    )
    const shouldShowBottomZone =
      freeCancellationMessage ||
      upsellNotice ||
      deltaFreeMessaging ||
      PartnerCardMerchandising ||
      formattedIconRowContent

    const bottomZoneContent = (
      <div>
        {freeCancellationMessage}
        {tripType !== 'OW' && upsellNotice}
        {tripType === 'OW' && formattedIconRowContent}
        {deltaFreeMessaging}
      </div>
    )

    return (
      <StyledItinerary
        data-test='styled-itinerary-card'
        className={itineraryLevelCssClasses}
        data-id={position}
        onClick={handleClick}
        bestValueFlight={bestValueFlight}
        hideCtaButton={hideCtaButton}
        isCachedListings={isCachedListings}
      >
        {!bestValueFlight &&
        (showFlightSale || merchandisingSection || isCugEligible || noChangeBadgeSection) ? (
          <TopZone
            key={'tops-zone'}
            isHidden={!(showFlightSale || merchandisingSection || noChangeBadgeSection)}
          >
            <Flex>
              {noChangeBadgeSection}
              {merchandisingSection}
            </Flex>

            {showFlightSale ? (
              <SaleBanner
                borderRadius='full'
                shouldShowMerchBadgeAndPkgDiscount={shouldShowMerchBadgeAndPkgDiscount}
                bannerMessage={saleBadgeText}
                savingsMessage={saleCopyText}
                bg={get(listingsSaleStyle, 'bg')}
                isPackageVIPDiscount={isPackageVIPDiscount}
              />
            ) : null}
            {cugSection}
          </TopZone>
        ) : null}

        {bestValueFlight && (
          <BestValueFlightTopZone
            key={'tops-zone'}
            data-test='best-value-flight-top-zone'
            borderRadius='lg'
            rounded='top'
          >
            <Text.span bold fontWeight='bold' mr='5px'>
              Recommended!
            </Text.span>
            <MobileText>Based off price and duration.</MobileText>
            <DesktopText>Best flight based off price and duration.</DesktopText>
          </BestValueFlightTopZone>
        )}

        <MainZone key={'main-zone'} data-test='main-zone'>
          <StyledItinSlices data-test='slices-section'>
            {slices}
            <OperatedBy
              pl={12}
              operatedByAirlines={operatedByAirlines}
              isSubjectToGovtApproval={isSubjectToGovtApproval}
            />
            <GovernmentApproval
              isSubjectToGovtApproval={isSubjectToGovtApproval}
              data-test='government-approval'
            >
              Subject to Government Approval
            </GovernmentApproval>
          </StyledItinSlices>

          {isCachedListings && (
            <Box width={'80px'} ml={'auto'} pr={'12px'}>
              <WidthSkeleton height={'28px'} />
            </Box>
          )}

          {!isCachedListings && (
            <PriceBoxContainer hideCtaButton={hideCtaButton}>
              {/* SETI: AIR_CUG_LOCK_MOBILE */}
              {isCugEligible && showCUGMobileLock && (
                <CugMobileLockIcon
                  color='primary.dark'
                  size={20}
                  name={isUserLoggedIn ? 'unlock' : 'lock'}
                  data-test='cug-eligible-lock'
                  onClick={isUserLoggedIn ? () => {} : openLoginModal}
                />
              )}
              <PriceBox
                isRTSliceBySlice={isRTSliceBySlice}
                priceDisplay={totalPriceWithDecimal}
                slices={itinerary.slices}
                seatsAvailable={itinerary.seatsAvailable}
                strikethrough={cugStrikeThroughPrice}
                incrementalPrice={itinerary.incrementalPrice}
                isCugEligible={isCugEligible}
                isSaleEligible={isSaleEligible}
                listingIsLoading={listingIsLoading}
                outBoundFlight={outBoundFlight}
                minimumRetailPrice={minimumRetailPrice}
                srText={srText}
                hideCtaButton={hideCtaButton}
              />
            </PriceBoxContainer>
          )}
        </MainZone>

        {/* Bottom zone, NON-FAREBRANDS */}
        {shouldShowBottomZone && (
          <BottomZone key={'bottom-zone'}>
            <Flex
              flexDirection={['column', null, null, 'row']}
              alignItems={['flex-start', null, null, 'center']}
            >
              {bottomZoneContent}
              <Flex ml={[0, null, null, 'auto']}>{PartnerCardMerchandising}</Flex>
              {listingCardPromotion}
            </Flex>
          </BottomZone>
        )}
      </StyledItinerary>
    )
  }
}

Itinerary.defaultProps = {
  listingIsLoading: false,
  brandReference: [],
  slices: [],
  allFareBrandNames: [],
  allFareBrandAttributes: [],
}

Itinerary.propTypes = {
  itinerary: PropTypes.object.isRequired,
  gaIndex: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  listingIsLoading: PropTypes.bool,
  openLoginModal: PropTypes.func,
  airportToCity: PropTypes.object,
  listingsSaleStyle: PropTypes.shape({
    bg: PropTypes.string,
    badgeText: PropTypes.string,
    copyText: PropTypes.string,
  }),
  showCUGMobileLock: PropTypes.bool,
  outBoundFlight: PropTypes.bool,
  isRTSliceBySlice: PropTypes.bool,
  brandReference: PropTypes.array,
  hasNoHoverTooltipCookie: PropTypes.bool,
  isCachedListings: PropTypes.bool,
  isUserLoggedIn: PropTypes.bool,
  setNoTooltipOnHoverCookie: PropTypes.func,
  slices: PropTypes.array,
  showAirportToolTip: PropTypes.bool,
  minimumRetailPrice: PropTypes.number,
  searchReturnedExpressDeals: PropTypes.bool,
  departingFrom: PropTypes.object,
  shouldShowMerchBadgeAndPkgDiscount: PropTypes.bool,
  hideCtaButton: PropTypes.bool,
  showNoChangesBadge: PropTypes.bool,
  searchRequestCabinClass: PropTypes.string,
  isPackageVIPDiscount: PropTypes.bool,
  listingCardPromotion: PropTypes.node,
}

Itinerary.contextTypes = {
  onSelect: PropTypes.func,
  SETI: PropTypes.object,
  flyGaLogic: PropTypes.object,
}

Itinerary.childContextTypes = {
  gaIndex: PropTypes.number,
}

export default Itinerary
